import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "main" }
const _hoisted_2 = { class: "container" }

import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { updateAuctionListeners, removeAllListeners } from "@/utils/websockets";
  import Catalog from "@/components/Catalog.vue";
  import LotMainInfo from "@/components/LotMainInfo.vue";
  import CustomHeader from "@/components/layouts/CustomHeader.vue";
  import ActionsTab from "@/components/action-block/ActionsTab.vue";
  import Preloader from "@/components/_partials/Preloader.vue";
  import LotMainSeller from "@/components/seller/LotMainSeller.vue";
  import apiClient from "@/services/api";
  import Toast from "primevue/toast";
  import { useToast } from "primevue/usetoast";
  import HotKeysService from "@/components/seller/HotKeysService.vue";
  import LiveAlertNotification from "@/components/_partials/LiveAlertNotification.vue";
  import { trans } from "@/utils/trans";
  import AuctionStatuses from "@/enum/AuctionStatuses";

  type ScreenWakeLockSentinel = EventTarget & {
    release: () => Promise<void>;
  };

  type NavigatorWithWakeLock = Navigator & {
    wakeLock?: {
      request: (type: "screen") => Promise<ScreenWakeLockSentinel>;
    };
  };

  
export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

  const auctionStore = useAuctionStore();
  const toast = useToast();
  const emitter = inject("emitter");

  const isDataLoaded = computed(() => auctionStore.translations && auctionStore.auction?.id);
  const shouldKeepScreenAwake = computed(
    () => isDataLoaded.value && auctionStore.auction.status === AuctionStatuses.LIVE
  );
  const changeStatusTimer = ref<null | number>(null);
  const screenWakeLock = ref<ScreenWakeLockSentinel | null>(null);
  let isAcquiringWakeLock = false;
  const state = reactive({
    alert: {
      intervalId: null as number | null,
      alertMessage: "" as string | null,
      leftBefore: null as number | null
    },
    chatFavicon: {
      intervalId: null as number | null,
      originalHref: null as string | null,
      alertHref: null as string | null,
      isAlertVisible: false
    }
  });

  onMounted(async () => {
    addPageStatusListeners();

    window.Echo.connector.pusher.connection.bind("connecting", () => {
      console.log("connecting...");
      removeAllListeners();
    });
    window.Echo.connector.pusher.connection.bind("connected", (payload: any) => {
      console.log("pusher connected!", payload);
      loadAuction();
    });

    auctionStore.initFilter();
    auctionStore.fetchMenu();
  });

  onBeforeUnmount(() => {
    removePageStatusListeners();
    releaseScreenWakeLock();
    stopChatFaviconBlink();
  });

  watch(shouldKeepScreenAwake, (shouldLock) => {
    if (shouldLock) {
      requestScreenWakeLock();
    } else {
      releaseScreenWakeLock();
    }
  });

  const loadAuction = async () => {
    auctionStore.$patch({
      lots: []
    });

    await auctionStore.fetchAuction().then(async () => {
      setTitle();
      updateAuctionListeners(toast, emitter);
      await auctionStore.syncActiveLotSnapshot();
      auctionStore.syncTimeLine();
      auctionStore.fetchLots();
      apiClient.post(`/live/user/${auctionStore.auction.id}/active`);
    });
  };

  const updatePageActiveState = () => {
    auctionStore.$patch({
      pageIsActive: !document.hidden && document.hasFocus()
    });
  };

  const addPageStatusListeners = () => {
    document.addEventListener("visibilitychange", updatePageActiveState);
    document.addEventListener("visibilitychange", handleWakeLockVisibilityChange);
    document.addEventListener("pointerup", requestScreenWakeLock, { passive: true });
    document.addEventListener("touchend", requestScreenWakeLock, { passive: true });
    document.addEventListener("keydown", requestScreenWakeLock, { passive: true });
    window.addEventListener("focus", updatePageActiveState);
    window.addEventListener("blur", updatePageActiveState);
    updatePageActiveState();
  };

  const removePageStatusListeners = () => {
    document.removeEventListener("visibilitychange", updatePageActiveState);
    document.removeEventListener("visibilitychange", handleWakeLockVisibilityChange);
    document.removeEventListener("pointerup", requestScreenWakeLock);
    document.removeEventListener("touchend", requestScreenWakeLock);
    document.removeEventListener("keydown", requestScreenWakeLock);
    window.removeEventListener("focus", updatePageActiveState);
    window.removeEventListener("blur", updatePageActiveState);
  };

  // Browsers auto-release the screen wake lock when the document becomes
  // hidden, so re-acquire keyed on visibility — NOT on focus/pageIsActive: a
  // tab returning to the foreground on mobile may stay unfocused, and a
  // focus-gated trigger would then miss the re-acquire.
  const handleWakeLockVisibilityChange = () => {
    if (!document.hidden) {
      requestScreenWakeLock();
    }
  };

  const requestScreenWakeLock = async () => {
    const wakeLock = (navigator as NavigatorWithWakeLock).wakeLock;

    if (!wakeLock || !shouldKeepScreenAwake.value || document.hidden || screenWakeLock.value || isAcquiringWakeLock) {
      return;
    }

    isAcquiringWakeLock = true;

    try {
      const lock = await wakeLock.request("screen");

      // Intent may have changed while awaiting the request; drop the lock if it
      // is no longer wanted instead of holding the screen awake against state.
      if (!shouldKeepScreenAwake.value || document.hidden) {
        await lock.release();
        return;
      }

      screenWakeLock.value = lock;
      lock.addEventListener("release", () => {
        if (screenWakeLock.value !== lock) {
          return;
        }

        screenWakeLock.value = null;

        // The platform can release the lock on its own (e.g. power-saving). If
        // it is still wanted and the page is visible, re-acquire it.
        if (shouldKeepScreenAwake.value && !document.hidden) {
          requestScreenWakeLock();
        }
      });
    } catch {
      // Wake lock is best-effort; a failed request (e.g. no user gesture or a
      // policy denial) simply leaves the screen able to sleep.
    } finally {
      isAcquiringWakeLock = false;
    }
  };

  const releaseScreenWakeLock = async () => {
    const lock = screenWakeLock.value;
    screenWakeLock.value = null;

    if (!lock) {
      return;
    }

    try {
      await lock.release();
    } catch {
      // Release is best-effort; the sentinel is already discarded either way.
    }
  };

  watch(
    () => auctionStore.pageIsActive,
    (nv, ov) => {
      if (auctionStore.auction.id && !auctionStore.user?.is_admin) {
        if (changeStatusTimer.value) {
          clearTimeout(changeStatusTimer.value);
          changeStatusTimer.value = null;
        }
        if (ov !== nv) {
          changeStatusTimer.value = setTimeout(() => {
            const status = nv ? "active" : "inactive";
            apiClient.post(`/live/user/${auctionStore.auction.id}/${status}`);
          }, 2000);
        }
      }
      if (ov !== nv && nv && state.alert.intervalId) {
        clearInterval(state.alert.intervalId);
        state.alert.leftBefore = null;
        state.alert.alertMessage = null;
        setTitle();
      }
    }
  );

  watch([() => auctionStore.hasNewChatMessage, () => auctionStore.pageIsActive], () => {
    syncChatFaviconBlink();
  });

  emitter?.on("auction:lot-alert", (data: { content: string }) => {
    if (!auctionStore.pageIsActive) {
      state.alert.alertMessage = data.content;

      if (state.alert.intervalId) {
        clearInterval(state.alert.intervalId);
      }

      state.alert.intervalId = setInterval(() => {
        if (document.title === state.alert.alertMessage) {
          setTitle();
        } else if (state.alert.alertMessage) {
          document.title = state.alert.alertMessage;
        }
      }, 1000);
    }
  });

  const setTitle = () => {
    document.title = trans("live.common.title", {
      title: auctionStore.auction.title,
      app_name: process.env.VUE_APP_APP_NAME
    });
  };

  const syncChatFaviconBlink = () => {
    if (auctionStore.hasNewChatMessage && !auctionStore.pageIsActive) {
      startChatFaviconBlink();
    } else {
      stopChatFaviconBlink();
    }
  };

  const startChatFaviconBlink = () => {
    if (state.chatFavicon.intervalId) {
      return;
    }

    const favicon = getFaviconElement();
    state.chatFavicon.originalHref = state.chatFavicon.originalHref || favicon.href;
    state.chatFavicon.alertHref = state.chatFavicon.alertHref || createChatAlertFavicon();
    favicon.href = state.chatFavicon.alertHref;
    state.chatFavicon.isAlertVisible = true;

    state.chatFavicon.intervalId = setInterval(() => {
      const nextHref = state.chatFavicon.isAlertVisible ? state.chatFavicon.originalHref : state.chatFavicon.alertHref;

      if (nextHref) {
        favicon.href = nextHref;
        state.chatFavicon.isAlertVisible = !state.chatFavicon.isAlertVisible;
      }
    }, 1000);
  };

  const stopChatFaviconBlink = () => {
    if (state.chatFavicon.intervalId) {
      clearInterval(state.chatFavicon.intervalId);
      state.chatFavicon.intervalId = null;
    }

    if (state.chatFavicon.originalHref) {
      getFaviconElement().href = state.chatFavicon.originalHref;
    }

    state.chatFavicon.isAlertVisible = false;
  };

  const getFaviconElement = (): HTMLLinkElement => {
    let favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');

    if (!favicon) {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      favicon.href = "/favicon.ico";
      document.head.appendChild(favicon);
    }

    return favicon;
  };

  const createChatAlertFavicon = (): string => {
    const canvas = document.createElement("canvas");
    const size = 32;
    const context = canvas.getContext("2d");

    canvas.width = size;
    canvas.height = size;

    if (!context) {
      return "/favicon.ico";
    }

    context.fillStyle = "#ff0000";
    context.beginPath();
    context.arc(size / 2, size / 2, 15, 0, Math.PI * 2);
    context.fill();

    context.strokeStyle = "#ffffff";
    context.lineWidth = 3;
    context.lineJoin = "round";
    context.strokeRect(8, 10, 16, 12);
    context.beginPath();
    context.moveTo(8, 10);
    context.lineTo(16, 17);
    context.lineTo(24, 10);
    context.stroke();

    return canvas.toDataURL("image/png");
  };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isDataLoaded.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createVNode(CustomHeader),
          _createElementVNode("main", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              (isDataLoaded.value)
                ? (_openBlock(), _createElementBlock("section", {
                    key: 0,
                    class: _normalizeClass([!_unref(auctionStore).user?.is_admin ? 'live-auction' : 'live-auction-seller'])
                  }, [
                    (_unref(auctionStore).user?.is_admin)
                      ? (_openBlock(), _createBlock(LotMainSeller, { key: 0 }))
                      : (_openBlock(), _createBlock(LotMainInfo, { key: 1 })),
                    _createVNode(Catalog),
                    _createVNode(ActionsTab)
                  ], 2))
                : _createCommentVNode("", true)
            ])
          ])
        ], 64))
      : (_openBlock(), _createBlock(Preloader, {
          key: 1,
          class: "load-app"
        })),
    (isDataLoaded.value && _unref(auctionStore).user?.is_admin)
      ? (_openBlock(), _createBlock(HotKeysService, { key: 2 }))
      : _createCommentVNode("", true),
    _createVNode(LiveAlertNotification),
    _createVNode(_unref(Toast))
  ], 64))
}
}

})